// The Studio's lightbox (Daniil's request, spec docs/specs/web/G1-screens.md decision 28): My
// Generations' delivered images or videos in the shared full-screen preview. Live's preview of a
// generation (`generations/preview-modal`, `video-preview-modal`, read for behaviour) shows who
// made it and when, steps through one job's outputs, offers Save, Save to project and Download on
// a video, and on an image the prompt ("This image doesn't have a prompt or structure yet." with
// none); ours keeps that and steps through the list's order across its jobs, its next page read
// near the end. The top bar has the card's own actions on the output shown (Save, Save to
// project, Download, Tweak and Save Prompt on an image) and the row's "Delete generation"; ⓘ shows
// how the output was made, as every lightbox's ⓘ does (`GenerationFacts`, decision 30), with its
// size or length. An output still on its way, or failed, is not in it.
import { ActionIcon, Text, Tooltip } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { Check, Download, FolderOpen, Save, SquarePlay, Trash2, Type, WandSparkles } from "lucide-react";

import { useListMembers } from "../../api/generated/endpoints/teams/teams";
import type { GenerationOutput, Job } from "../../api/generated/model";
import { memberName } from "../../platform/members";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useRowActions } from "../../platform/jobs/GenerationActions";
import { outputKey } from "../../platform/jobs/output-picks";
import { GenerationFacts } from "../../platform/media/GenerationFacts";
import { MediaPreview, type PreviewItem } from "../../platform/media/MediaPreview";
import { formatDay } from "../../ui/format";
import { typeLabel } from "../../platform/studio/draft";
import type { Generation } from "./generations";
import { PROMPT_SAVED, SIMPLE_TYPES, useSavePrompt } from "../../platform/media/save-prompt";
import { previewKind } from "../../platform/media/output-kind";
import classes from "./GenerationPreview.module.css";

/** A delivered output the lightbox shows, with its job and its place in its row (from 0). */
export interface PreviewOutput {
  item: PreviewItem;
  job: Job;
  output: GenerationOutput;
  place: number;
}

/** The lightbox's id of an output: its job's and its index. */
export function previewId(job: Job, output: GenerationOutput): string {
  return outputKey({ jobId: job.id, index: output.index });
}

/** An output's name on the bar: its prompt's start, or its type, and its number in its row. */
function nameOf(job: Job, kind: "image" | "video", place: number): string {
  const prompt = job.params?.prompt?.trim() ?? "";
  const what = prompt === "" ? typeLabel(job.type) : prompt.length > 60 ? `${prompt.slice(0, 59)}…` : prompt;
  return `${what} (${kind} ${place + 1})`;
}

/**
 * The rows' delivered outputs with a picture, in the list's order: each job's by their index, a
 * talking head's by their place in its row (as the row numbers them). One still on its way, or
 * failed, is left out.
 */
export function previewOutputs(generations: readonly Generation[]): PreviewOutput[] {
  return generations.flatMap(({ jobs }) => {
    const outputs = jobs.flatMap((job) => (job.error === null ? job.outputs.map((output) => ({ job, output })) : []));
    return outputs.flatMap(({ job, output }, at) => {
      const kind = previewKind(output);
      if (output.status !== "delivered" || output.url === null || kind === null) return [];
      const place = jobs.length === 1 ? output.index : at;
      const item: PreviewItem = {
        id: previewId(job, output),
        name: nameOf(job, kind, place),
        kind,
        url: output.url,
        // The strip's lighter pictures (issue 828, item 12): the thumbnail, else the image itself.
        thumb: output.thumb_url ?? (kind === "image" ? output.url : null),
        poster: output.poster_url ?? null,
        durationMs: output.duration_ms,
        width: output.width,
        height: output.height,
      };
      return [{ item, job, output, place }];
    });
  });
}

export interface GenerationPreviewProps {
  teamId: string;
  brandId: string;
  outputs: readonly PreviewOutput[];
  current: string | null;
  onShow: (id: string | null) => void;
  more: { has: boolean; loading: boolean; load: () => void };
  /** Reads the list again, for fresh URLs (a download's link expired). */
  readAgain: () => void;
}

export function GenerationPreview(props: GenerationPreviewProps) {
  const { outputs } = props;
  const members = useListMembers(props.teamId).data ?? [];
  const find = (item: PreviewItem) => outputs.find((each) => each.item.id === item.id);
  // Live's header: who made it ("Unknown" when no one) and the day.
  const maker = (job: Job) =>
    job.created_by === null ? "Unknown" : memberName(members.find((member) => member.user_id === job.created_by));
  return (
    <MediaPreview
      items={outputs.map((each) => each.item)}
      current={props.current}
      onShow={props.onShow}
      more={props.more}
      byline={(item) => {
        const shown = find(item);
        return shown === undefined ? null : `${maker(shown.job)} · ${formatDay(shown.job.created_at)}`;
      }}
      actions={(item) => {
        const shown = find(item);
        return shown === undefined ? null : (
          <OutputActions key={item.id} {...shown} teamId={props.teamId} brandId={props.brandId} readAgain={props.readAgain} />
        );
      }}
      details={(item) => {
        const shown = find(item);
        return shown === undefined ? null : (
          <GenerationFacts
            teamId={props.teamId}
            brandId={props.brandId}
            job={shown.job}
            output={shown.output}
            onLeave={() => props.onShow(null)}
          />
        );
      }}
    />
  );
}

/**
 * The output's actions, as its card offers them (`ImageCardActions`): Save, Save to project,
 * Download, and on an image Tweak and Save Prompt (a saved picture of a simple generation); then
 * the row's "Delete generation", which deletes the job and every output of it.
 */
function OutputActions(props: PreviewOutput & { teamId: string; brandId: string; readAgain: () => void }) {
  const { teamId, brandId, job, output, place } = props;
  const actions = useRowActions({ teamId, brandId, jobs: [job], readAgain: props.readAgain });
  const prompt = useSavePrompt(teamId, brandId);
  const pick = { jobId: job.id, index: output.index };
  const saved = actions.saved.has(outputKey(pick)) || (output.saved_asset_id ?? null) !== null;
  const picture = output.saved_asset_id ?? null;
  const promptable = previewKind(output) === "image" && picture !== null && SIMPLE_TYPES.includes(job.type);
  const number = place + 1;
  const icon = { variant: "subtle", color: "gray", c: "var(--cai-color-viewer-text)" } as const;
  return (
    <>
      <Tooltip label={saved ? "Saved" : "Save"}>
        <ActionIcon
          {...icon}
          aria-label={saved ? `Output ${number} saved` : `Save output ${number}`}
          disabled={saved || actions.saving}
          onClick={() => actions.saveOne(pick)}
        >
          {saved ? <Check size={18} aria-hidden /> : <Save size={18} aria-hidden />}
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Save to project">
        <ActionIcon
          {...icon}
          aria-label={`Save output ${number} to project`}
          disabled={saved || actions.saving}
          onClick={() => actions.saveToProject(pick)}
        >
          <FolderOpen size={18} aria-hidden />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Download">
        <ActionIcon {...icon} aria-label={`Download output ${number}`} onClick={() => actions.downloadOne(job, output)}>
          <Download size={18} aria-hidden />
        </ActionIcon>
      </Tooltip>
      {previewKind(output) === "video" ? (
        // Beyond live (Daniil's; spec open-from-studio): the video in the editor, in a new tab.
        <Tooltip label="Open in the editor">
          <ActionIcon
            {...icon}
            aria-label={`Open output ${number} in the editor`}
            onClick={() => actions.openInEditor(job, output)}
          >
            <SquarePlay size={18} aria-hidden />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {previewKind(output) === "image" ? (
        <Tooltip label="Tweak">
          <ActionIcon
            {...icon}
            aria-label={`Tweak output ${number}`}
            renderRoot={(rootProps) => (
              <Link
                {...rootProps}
                to="/teams/$teamId/brands/$brandId/generate"
                params={{ teamId, brandId }}
                search={{ type: "image.tweak", job: job.id, output: output.index }}
              />
            )}
          >
            <WandSparkles size={18} aria-hidden />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {promptable ? (
        // Live's icon has no saved look: the notes say live's toast, or its 409 (issue 908).
        <Tooltip label="Save Prompt">
          <ActionIcon
            {...icon}
            aria-label={`Save the prompt of output ${number}`}
            loading={prompt.pending === picture}
            onClick={() => prompt.save(picture)}
          >
            <Type size={18} aria-hidden />
          </ActionIcon>
        </Tooltip>
      ) : null}
      <Tooltip label="Delete generation">
        <ActionIcon
          {...icon}
          aria-label="Delete generation"
          disabled={!actions.deletable}
          onClick={actions.deleteGeneration}
        >
          <Trash2 size={18} aria-hidden />
        </ActionIcon>
      </Tooltip>
      {actions.picker}
      {/* What a save, a download, a delete or a Save Prompt said, under the bar, as the row says it. */}
      {actions.notes === null && prompt.said === null && prompt.error === null ? null : (
        <div className={classes.notes}>
          {actions.notes}
          {prompt.said === null ? null : (
            <Text component="output" size="sm">
              {PROMPT_SAVED}
            </Text>
          )}
          {prompt.error === null ? null : <ErrorAlert error={prompt.error} />}
        </div>
      )}
    </>
  );
}
